import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map } from 'rxjs';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageService } from 'ng-zorro-antd/message';
import { MenuService } from '../core/menu.service';
import { RouteConfigService } from '../core/route-config.service';
import { ThemeService } from '../core/theme.service';
import { AuthService } from '../core/auth/auth.service';
import { CurrentUserService } from '../core/user/current-user.service';
import { UserMenuComponent } from './user-menu/user-menu.component';
import { IconComponent } from '../shared/components/icon/icon.component';
import { useIsPhone } from '../shared/composables/use-is-phone.composable';
import { MenuSection, buildMenuSections, isUrlInGroup, opensActiveSection, resolveSectionOptions, sectionIcon } from '../shared/utils/menu-groups.utils';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    RouterOutlet, RouterLink, RouterLinkActive, NgTemplateOutlet,
    NzMenuModule, NzDropdownModule, NzPopoverModule, NzDrawerModule, UserMenuComponent, IconComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit {
  menuService          = inject(MenuService);
  themeService         = inject(ThemeService);
  authService          = inject(AuthService);
  currentUserRole      = signal<string | null>(null);
  private routeConfig  = inject(RouteConfigService);
  private currentUser  = inject(CurrentUserService);
  private msg          = inject(NzMessageService);
  private router       = inject(Router);

  // Phone-width viewport (<576px) — mirrors @screen-xs-max in shared/styles/breakpoints.less.
  // Below this the floating sidebar is replaced by an off-canvas nz-drawer.
  isPhone = useIsPhone();

  // Ephemeral UI-only state (not persisted/restorable) — plain signals, not a Config/State pair:
  // LayoutComponent is the app shell, not one of the reusable config-driven components.
  // Sidebar starts as the icon rail (STILE-VISUALE.md §4.1), leaving the content its full width.
  isCollapsed    = signal(true);
  mobileMenuOpen = signal(false);

  // The menu as the sidebar draws it. Every header is a section, whichever way the ui_config spells it ('group' followed by its
  // items, or 'submenu' with children), so the hand-written menu and the generated one behave the same.
  menuNodes = computed(() => buildMenuSections(this.menuService.items()));

  // The rail (narrow sidebar, desktop/tablet) has no room for headers: a section is one icon that opens its entries in a flyout.
  // Everywhere else (expanded sidebar, phone drawer) a section is an accordion.
  railMode = computed(() => this.isCollapsed() && !this.isPhone());

  // Router URL, to open (and highlight) the section that holds the page being shown.
  private currentUrl = toSignal(
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), map(e => e.urlAfterRedirects)),
    { initialValue: this.router.url },
  );

  // What the user (or the opening of the current page) decided about a section, by sectionKey: true = open, false = closed.
  // A section with no entry here follows the configuration: its own `expanded`, else the `defaults` of the menu, else closed.
  // Ephemeral like the rest of the shell state.
  private sectionChoices = signal<ReadonlyMap<string, boolean>>(new Map());

  // Opens the section of the current page on load and at every navigation, when the menu asks for it (`defaults.openActiveSection`,
  // on unless configured otherwise); the user can still close it afterwards.
  private openSectionOfCurrentPage = effect(() => {
    const url = this.currentUrl();
    if (!opensActiveSection(this.menuService.defaults())) {
      return;
    }
    this.menuNodes().forEach((node, index) => {
      if (node.kind === 'section' && isUrlInGroup(url, node.entries)) {
        const key = this.sectionKey(node, index);
        untracked(() => this.sectionChoices.update(choices => choices.get(key) === true ? choices : new Map(choices).set(key, true)));
      }
    });
  });

  readonly iconOf = sectionIcon;

  /** Identity of a section: the label alone is not enough when two sections share it. */
  sectionKey(section: MenuSection, index: number): string {
    return `${index}:${section.label}`;
  }

  /** Whether the section can be opened and closed, and whether it starts open: its own setting, else the menu's defaults, else built in. */
  optionsOf(section: MenuSection): { collapsible: boolean; expanded: boolean } {
    return resolveSectionOptions(section, this.menuService.defaults());
  }

  /** A section that is not collapsible is always open; otherwise the choice made so far, else the configured initial state. */
  isSectionExpanded(section: MenuSection, index: number): boolean {
    const options = this.optionsOf(section);
    return !options.collapsible || (this.sectionChoices().get(this.sectionKey(section, index)) ?? options.expanded);
  }

  /** The page being shown belongs to this section: its header (or its rail icon) is highlighted, also while it is closed. */
  isSectionActive(section: MenuSection): boolean {
    return isUrlInGroup(this.currentUrl(), section.entries);
  }

  toggleSection(section: MenuSection, index: number): void {
    const key = this.sectionKey(section, index);
    const open = this.isSectionExpanded(section, index);
    this.sectionChoices.update(choices => new Map(choices).set(key, !open));
  }

  // Close the drawer if the viewport grows back out of phone width while it's open.
  private closeMobileMenuOnDesktop = effect(() => {
    if (!this.isPhone()) this.mobileMenuOpen.set(false);
  });

  ngOnInit(): void {
    // APP_INITIALIZER runs before the DOM renders, so toasts fired there are lost.
    // Errors are stored in signals during init and displayed here, after render.
    const menuErr  = this.menuService.loadError();
    const routeErr = this.routeConfig.loadError();
    if (menuErr)  this.msg.error(menuErr);
    if (routeErr) this.msg.error(routeErr);

    this.currentUser.getMe().subscribe(profile => this.currentUserRole.set(profile.roleName));
  }
}
